import axios, {CancelTokenSource} from 'axios';
import {AddToast} from "react-toast-notifications";

export interface iCsrfToken {
    token: string
    expiry: Date
}

export interface iApiBasicResponse {
    statusCode: number
    message?: string
    validation_errors?: {
        [key: string]: string | Array<string>
    }
}

export interface iBasicListingResponse<T> extends iApiBasicResponse {
    current_page: number
    data: Array<T>
    length: number
    first_page_url: string
    from: number
    last_page: number
    last_page_url: string
    next_page_url: string
    path: string
    per_page: string
    to: number
    total: number,

}

export interface iCsrfTokenResponse extends iApiBasicResponse {
    token?: string
}
interface iFormData {
    key: string
    value: string
}

export interface iMultilevelStringOrNumber {
    [key: string]: undefined | string | string[] | number | number[] | boolean | boolean[] | iMultilevelStringOrNumber | iMultilevelStringOrNumber[]
}

/**
 * This class is used to 'flatten' the incoming objects:
 * Example CreationParams:
 * {
 *     name: John
 *     address:{
 *         country: Germany
 *         city: Munich
 *         location: {
 *             lat: 33.5445
 *             lng: 46.9956
 *         }
 *     }
 * }
 *
 * Example 'flattened' output:
 * [
 *  {key: 'name', value: 'John'},
 *  {key: 'address[country]', value: 'Germany'},
 *  {key: 'address[city]', value: 'Munich'},
 *  {key: 'address[location][lat]', value: '33.5445'},
 *  {key: 'address[location][lng]', value: '46.9956'},
 * ]
 */

export class ObjectFlatter {
    private readonly flattened_data: Array<iFormData>;

    constructor(params: iMultilevelStringOrNumber) {
        this.flattened_data = [];
        this.process_params(params, "");
    }

    process_params = (params: iMultilevelStringOrNumber, suffix: string) => {
        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                const key_with_suffix = suffix ? (suffix + '[' + key + ']') : key;
                const value = params[key];
                if (typeof value === 'string') {
                    this.flattened_data.push({key: key_with_suffix, value: value});
                } else if (typeof value === 'number') {
                    this.flattened_data.push({key: key_with_suffix, value: value + ""});
                } else if (typeof value === 'boolean') {
                    this.flattened_data.push({key: key_with_suffix, value: (value ? "true" : "false")});
                } else if (typeof value === 'undefined') {
                    // Do nothing.
                } else {
                    // Check for arrays.
                    let value_object: iMultilevelStringOrNumber;
                    if (Array.isArray(value)) {
                        value_object = {};
                        value.forEach((val: any, index: number) => value_object[index + ''] = val);
                    } else {
                        value_object = value;
                    }
                    this.process_params(value_object, key_with_suffix);
                }
            }
        }
    };

    get_form_data = (): Array<iFormData> => {
        return this.flattened_data
    };
}
export default class BaseAPIs {
    static csrfToken: iCsrfToken = {
        token: "dummy_expired_token",
        expiry: new Date(0)
    };

    static hasError = (response: iApiBasicResponse,addToast?:AddToast,removeAllToasts?:any) => {

        if(response && response.message && addToast){
            removeAllToasts && removeAllToasts();
        }
        if(!response || response.statusCode >= 400){
            response && response.message && addToast && addToast(response.message, {appearance: 'error',})
        }else {
            response && response.message && addToast && addToast(response.message, {appearance: 'success'})
        }
        if (!response || response.statusCode >= 400) return true;
        if (!response || response.validation_errors) return true;
    };

    static getError = (response: iApiBasicResponse): string => {
        if (response.message && typeof response.message === "string") return response.message;
        if (!response.validation_errors) return BaseAPIs.getErrorFromStatusCode(response);
        if (response.validation_errors.csrf) return BaseAPIs.getErrorString(response.validation_errors.csrf);
        if (response.validation_errors.network) return BaseAPIs.getErrorString(response.validation_errors.network);

        if (response.validation_errors.email) return BaseAPIs.getErrorString(response.validation_errors.email);
        if (response.validation_errors.password) return BaseAPIs.getErrorString(response.validation_errors.password);

        if (response.validation_errors.name) return BaseAPIs.getErrorString(response.validation_errors.name);
        return "Unknown error!";
    };

    static getErrorFromStatusCode = (response: iApiBasicResponse): string => {
        switch (response.statusCode) {
            case 400:
                return "Request error: Bad Request";
            case 401:
                return "Request error: Unauthorized";
            case 402:
                return "Request error: Payment Required";
            case 403:
                return "Request error: Forbidden";
            case 404:
                return "Request error: Not Found";
            case 405:
                return "Request error: Method Not Allowed";
            case 500:
                return "Server error: Internal error";
            case 501:
                return "Server error: Not Implemented";
            case 502:
                return "Server error: Bad Gateway";
            case 503:
                return "Server error: Service Unavailable";
            case 504:
                return "Server error: Gateway Timeout";
            default:
                return "Unknown error";
        }
    };

    private static getErrorString(error: string | Array<string>) {
        if (typeof error === "string") return error;
        else return error.join(" ");
    }

    getApiBaseURL = () => {
        const DEBUG = process.env.NODE_ENV !== 'production';
        return DEBUG ? "http://localhost:8000" : (window.location.protocol + "//" + window.location.hostname + ":" + window.location.port);
    };

    getCsrfToken = async (refresh_token: boolean = false,source?:CancelTokenSource): Promise<string> => {
        return this.fetchCsrfToken(source).then((r: iCsrfTokenResponse) => {
            if (BaseAPIs.hasError(r) || !r.token) {
                return "";
            } else {
                return r.token;
            }
        })
    };

    fetchCsrfToken = (source?:CancelTokenSource): Promise<iCsrfTokenResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/_api/v1/csrf-token", {
                withCredentials: true,
                cancelToken:source && source.token
            },)
            .then((res): iCsrfTokenResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
            });
    };
    ipLocation = (): Promise<any> => {
        return axios
            .get(this.getApiBaseURL() + "/_api/v1/ip-location", {
                withCredentials: true,
            },)
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
            });
    };
    handleCatch = (error: any) => {
        if (error && error.response && error.response.status === 401) {
            const confirm = window.confirm("It seems that you have been logged out. Press ok to login");
            if (confirm) {
                window.location.href="/login"
            }
        }
        if (error && error.response && error.response.status && error.response.data) {
            return {
                ...error.response.data, statusCode: error.response.status
            }
        } else if (error instanceof axios.Cancel) {
            return {statusCode: 999, message: undefined};
        } else {
            return {statusCode: 999, message: "Network Error"};
        }
    }
}
